import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/primitives/dialog";
import { useImageUploader } from "@/middleware/hooks/useImageUploader";
import { Feedback, getFeedbackLabels } from "@/server/types/feedback";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/primitives/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/primitives/select";
import { Textarea } from "@/components/primitives/textarea";
import FileUploader from "./FileUploader";
import { Button } from "@/components/primitives/button";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuthState } from "@/zustand/AuthState";
import { useForm } from "react-hook-form";
import { FeedbackFormSchema, FeedbackFormType } from "@/schemas/feedback";
import { zodResolver } from "@hookform/resolvers/zod";
import { sendFeedback } from "@/server/services/feedbackService";
import { toast } from "@/hooks/useToast";

interface AddFeedbackProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Optional structured diagnostic blob. When supplied, it is appended (as
   * JSON) to the message body so the support team can see app shape/state
   * without the user having to copy/paste anything. NEVER include
   * ciphertext, plaintext, or keys in this object.
   */
  diagnostics?: Record<string, unknown>;
  /**
   * Optional default message. Combined with `diagnostics` when both are
   * supplied.
   */
  defaultMessage?: string;
}
const AddFeedback: React.FC<AddFeedbackProps> = ({
  open,
  onOpenChange,
  diagnostics,
  defaultMessage,
}: AddFeedbackProps) => {
  const username = useAuthState((state) => state.username);
  const { t } = useTranslation();
  const {
    isUploading,
    filename,
    attachmentId,
    handleFileChange,
    deleteFile,
    resetUploader,
  } = useImageUploader({ fileSizeLimit: 10 * 1024 * 1024 });

  const initialMessage = (() => {
    const base = defaultMessage ?? "";
    if (!diagnostics) {
      return base;
    }
    // Defensive: callers shouldn't pass non-serializable values, but a
    // circular reference / BigInt slipping through here would otherwise
    // crash the dialog mid-render. Fall back to a placeholder so the user
    // can still file the report.
    let json: string;
    try {
      json = JSON.stringify(diagnostics, null, 2);
    } catch {
      json = "[diagnostics could not be serialised]";
    }
    return base
      ? `${base}\n\n--- diagnostics ---\n${json}`
      : `--- diagnostics ---\n${json}`;
  })();

  const form = useForm<FeedbackFormType>({
    resolver: zodResolver(FeedbackFormSchema),
    defaultValues: {
      type: -1,
      message: initialMessage,
    },
  });

  // `useForm({ defaultValues })` is only honored on the first render; new
  // diagnostics/defaultMessage props passed when the dialog reopens would
  // otherwise be ignored. Reset on the false → true open transition only,
  // so we don't clobber what the user has typed if `diagnostics`/`defaultMessage`
  // arrive asynchronously while the dialog is already open.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      form.reset({ type: -1, message: initialMessage });
    }
    wasOpenRef.current = open;
    // `initialMessage` is intentionally not in deps: we capture the value at
    // the moment of the open transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function onSubmit(values: FeedbackFormType) {
    const content = {
      image: attachmentId,
      message: values.message,
    };

    const feedback: Feedback = {
      label: values.type,
      content: JSON.stringify(content),
      username: username,
    };

    try {
      await sendFeedback(feedback);
      form.reset();
      resetUploader();
      onOpenChange(false);
    } catch (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  }

  async function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      if (attachmentId) {
        await deleteFile();
      }
      form.reset();
      resetUploader();
    }
    onOpenChange(isOpen);
  }

  const items = getFeedbackLabels();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-description="Feedback form"
        aria-describedby="feedback"
      >
        <DialogHeader>
          <DialogTitle>{t("feedbackTitle")}</DialogTitle>

          <DialogDescription>
            <br />
            <p>{t("feedbackDescription")}</p>
            <br />
            <p>{t("feedbackNoReply")}</p>
            <br />
            <p>{t("feedbackThankYou")}</p>
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="my-7 space-y-2"
          >
            <div className="flex flex-col gap-5">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <Select onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue
                            placeholder={t("feedbackTypePlaceholder")}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {items.map((item) => (
                          <SelectItem
                            key={item.value}
                            value={item.value.toString()}
                          >
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        rows={5}
                        placeholder={t("feedbackPlaceholder")}
                        className="resize-none placeholder:opacity-30 focus:outline-blue-700"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FileUploader
                isUploading={isUploading}
                filename={filename}
                attachmentId={attachmentId}
                handleFileChange={handleFileChange}
                deleteFile={deleteFile}
              />
            </div>
            <Button
              disabled={form.formState.isSubmitting}
              type="submit"
              className="mt-0 w-full"
            >
              {form.formState.isSubmitting ? t("sendingFeedback") : t("submit")}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={async () => await handleOpenChange(false)}
            >
              {t("cancel")}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddFeedback;
